
import { useCallback, useEffect, useMemo, useState } from "react";

const API_BASE = import.meta?.env?.VITE_API_URL || "https://bmtvendorvendorfinal.onrender.com";
const CLUBS_URL = `${API_BASE}/api/vendor/nightclubs/my-club`;
const EVENTS_URL = `${API_BASE}/api/nightclub-events/my-events`;
const BOOKINGS_URL = `${API_BASE}/api/nightclub-event-bookings/vendor`;
const TABLES_URL = `${API_BASE}/api/vendor/nightclub-tables/my-tables`;

const getToken = () => {
  const v = localStorage.getItem("vendorToken");
  return v && v !== "null" && v !== "undefined" ? v : "";
};

async function api(url) {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) throw new Error(data.message || `Request failed (${res.status})`);
  return Array.isArray(data.data) ? data.data : [];
}

/* ---------- helpers ---------- */
const dayKey = (d) => (d ? new Date(d).toLocaleDateString("en-CA") : ""); // local YYYY-MM-DD
const utcKey = (d) => (d ? new Date(d).toISOString().slice(0, 10) : ""); // event dates are stored UTC
const todayKey = () => new Date().toLocaleDateString("en-CA");

const money = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;
const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", timeZone: "UTC" }) : "—";

const isLive = (b) => !["cancelled", "refunded"].includes(b.bookingStatus);
const isPaid = (b) => b.paymentStatus === "paid" && isLive(b);

const last7 = () =>
  Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    return { key: d.toLocaleDateString("en-CA"), label: d.toLocaleDateString("en-IN", { weekday: "short" }) };
  });

/* ---------- component ---------- */
export default function NightclubDashboard({ onNavigate = () => {} }) {
  const [clubs, setClubs] = useState([]);
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tableCount, setTableCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const [c, e, b] = await Promise.allSettled([api(CLUBS_URL), api(EVENTS_URL), api(BOOKINGS_URL)]);

    const clubList = c.status === "fulfilled" ? c.value : [];
    setClubs(clubList);
    setEvents(e.status === "fulfilled" ? e.value : []);
    setBookings(b.status === "fulfilled" ? b.value : []);

    const failed = [c, e, b].find((r) => r.status === "rejected");
    if (failed) setError(failed.reason?.message || "Kuch data load nahi ho paya");

    // tables: har club ke tables count karo
    try {
      const lists = await Promise.all(
        clubList.map((club) => api(`${TABLES_URL}?nightClubId=${encodeURIComponent(club._id)}`).catch(() => []))
      );
      setTableCount(lists.flat().filter((t) => t.isActive !== false).length);
    } catch {
      setTableCount(0);
    }

    setUpdatedAt(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* ---------- derived ---------- */
  const d = useMemo(() => {
    const today = todayKey();
    const live = bookings.filter(isLive);
    const paid = bookings.filter(isPaid);

    const todaysBookings = bookings.filter((b) => dayKey(b.createdAt) === today);
    const todaysRevenue = paid.filter((b) => dayKey(b.paidAt || b.createdAt) === today);
    const checkedInToday = bookings.filter((b) => b.entryStatus === "checked_in" && dayKey(b.checkedInAt) === today);

    const upcomingEvents = events
      .filter((ev) => utcKey(ev.eventDate) >= today && ev.status !== "cancelled")
      .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate));
    const tonight = upcomingEvents.filter((ev) => utcKey(ev.eventDate) === today);

    const guestsExpected = bookings
      .filter((b) => b.bookingStatus === "confirmed" && utcKey(b.eventId?.eventDate) >= today)
      .reduce((s, b) => s + (b.totalPersons || 0), 0);

    const pending = bookings
      .filter((b) => b.bookingStatus === "pending")
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const trend = last7().map((day) => ({
      ...day,
      revenue: paid.filter((b) => dayKey(b.paidAt || b.createdAt) === day.key).reduce((s, b) => s + (b.totalAmount || 0), 0),
      count: live.filter((b) => dayKey(b.createdAt) === day.key).length,
    }));

    return {
      todaysBookings: todaysBookings.length,
      todaysRevenue: todaysRevenue.reduce((s, b) => s + (b.totalAmount || 0), 0),
      totalRevenue: paid.reduce((s, b) => s + (b.totalAmount || 0), 0),
      awaiting: live.filter((b) => b.paymentStatus !== "paid").reduce((s, b) => s + (b.totalAmount || 0), 0),
      checkedIn: checkedInToday.length,
      guestsExpected,
      upcomingEvents,
      tonight,
      pending,
      trend,
      trendMax: Math.max(...trend.map((t) => t.revenue), 1),
      weekRevenue: trend.reduce((s, t) => s + t.revenue, 0),
      weekBookings: trend.reduce((s, t) => s + t.count, 0),
    };
  }, [bookings, events]);

  const clubStatus = useMemo(() => {
    const by = (s) => clubs.filter((c) => c.status === s).length;
    return {
      total: clubs.length,
      approved: by("approved"),
      pending: by("pending"),
      draft: by("draft"),
      rejected: by("rejected"),
      published: clubs.filter((c) => c.isPublished).length,
    };
  }, [clubs]);

  const checklist = [
    { done: clubs.length > 0, label: "Night club add karo", go: "nightclub_manage" },
    { done: clubStatus.approved > 0, label: "Club ko approval ke liye submit karo", go: "nightclub_manage" },
    { done: clubStatus.published > 0, label: "Club publish karo", go: "nightclub_manage" },
    { done: tableCount > 0, label: "Tables add karo", go: "nightclub_tables" },
    { done: events.length > 0, label: "Pehla event banao", go: "events_manage" },
    { done: events.some((ev) => ev.isPublished), label: "Event publish karo", go: "events_manage" },
    { done: bookings.length > 0, label: "Pehli booking receive karo", go: "nightclub_bookings" },
  ];
  const doneCount = checklist.filter((c) => c.done).length;

  const metrics = [
    { title: "Today's Bookings", value: d.todaysBookings, sub: `LAST 7 DAYS : ${d.weekBookings} BOOKINGS`, go: "nightclub_bookings" },
    { title: "Today's Revenue", value: money(d.todaysRevenue), sub: `LAST 7 DAYS : ${money(d.weekRevenue)}`, go: "nightclub_bookings" },
    { title: "Upcoming Events", value: d.upcomingEvents.length, sub: `TONIGHT : ${d.tonight.length} EVENT(S)`, go: "events_manage" },
    { title: "Guests Expected", value: d.guestsExpected, sub: `CHECKED IN TODAY : ${d.checkedIn}`, go: "nightclub_bookings" },
  ];

  return (
    <div className="ncd">
      <style>{CSS}</style>

      <section className="ncd-head">
        <div>
          <h1>Performance Overview</h1>
          <p>{updatedAt ? `Last updated ${updatedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : "Loading…"}</p>
        </div>
        <button className="ncd-btn" onClick={load} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </section>

      {error && <div className="ncd-alert">{error}</div>}

      <section className="ncd-banner">
        <div>
          <h2>Night Club Analytics</h2>
          <p>
            Total paid revenue <b>{money(d.totalRevenue)}</b> · Awaiting payment <b>{money(d.awaiting)}</b> · Pending
            confirmations <b>{d.pending.length}</b>
          </p>
        </div>
        <button className="ncd-btn light" onClick={() => onNavigate("nightclub_bookings")}>View Bookings</button>
      </section>

      <section className="ncd-metrics">
        {metrics.map((m) => (
          <article className="ncd-metric" key={m.title}>
            <h3>{m.title}</h3>
            <div className="ncd-value">{loading ? "…" : m.value}</div>
            <p>{m.sub}</p>
            <button onClick={() => onNavigate(m.go)}>View More</button>
          </article>
        ))}
      </section>

      <section className="ncd-grid">
        {/* Revenue trend */}
        <article className="ncd-card">
          <div className="ncd-card-head"><div><h3>Revenue – last 7 days</h3><p>Paid bookings only</p></div></div>
          <div className="ncd-chart">
            {d.trend.map((t) => (
              <div className="ncd-bar-col" key={t.key} title={`${t.label}: ${money(t.revenue)} • ${t.count} bookings`}>
                <span className="ncd-bar-val">{t.revenue ? money(t.revenue) : ""}</span>
                <div className="ncd-bar" style={{ height: `${Math.max((t.revenue / d.trendMax) * 100, 3)}%` }} />
                <span className="ncd-bar-label">{t.label}</span>
              </div>
            ))}
          </div>
        </article>

        {/* Club status + checklist */}
        <article className="ncd-card">
          <div className="ncd-card-head">
            <div><h3>Setup Progress</h3><p>{doneCount} of {checklist.length} steps done</p></div>
            <span className="ncd-badge">{clubStatus.total} club(s)</span>
          </div>
          <div className="ncd-progress"><i style={{ width: `${(doneCount / checklist.length) * 100}%` }} /></div>
          <div className="ncd-list">
            {checklist.map((c) => (
              <button key={c.label} className={c.done ? "done" : ""} onClick={() => onNavigate(c.go)}>
                <span className="ncd-tick">{c.done ? "✓" : ""}</span>
                <span>{c.label}</span>
                <b>›</b>
              </button>
            ))}
          </div>
          <div className="ncd-chips">
            <span>Approved {clubStatus.approved}</span>
            <span>Pending {clubStatus.pending}</span>
            <span>Draft {clubStatus.draft}</span>
            {clubStatus.rejected > 0 && <span className="bad">Rejected {clubStatus.rejected}</span>}
            <span>Tables {tableCount}</span>
          </div>
        </article>

        {/* Upcoming events */}
        <article className="ncd-card">
          <div className="ncd-card-head">
            <div><h3>Upcoming Events</h3><p>{d.tonight.length ? `${d.tonight.length} event(s) tonight` : "Next scheduled events"}</p></div>
            <button className="ncd-link" onClick={() => onNavigate("events_manage")}>Manage</button>
          </div>
          {d.upcomingEvents.length === 0 ? (
            <Empty text="Koi upcoming event nahi hai." action="Create Event" onClick={() => onNavigate("events_manage")} />
          ) : (
            <div className="ncd-rows">
              {d.upcomingEvents.slice(0, 5).map((ev) => {
                const total = ev.totalCapacity || 0;
                const sold = Math.max(total - (ev.availableCapacity ?? total), 0);
                const pct = total ? Math.round((sold / total) * 100) : 0;
                return (
                  <div className="ncd-row" key={ev._id}>
                    <div className="ncd-date">{fmtDate(ev.eventDate)}</div>
                    <div className="ncd-row-main">
                      <strong>{ev.title}</strong>
                      <small>{ev.nightClubId?.name || "—"} · {ev.startTime} – {ev.endTime}</small>
                      <div className="ncd-progress sm"><i style={{ width: `${pct}%` }} /></div>
                      <small>{sold}/{total} booked ({pct}%)</small>
                    </div>
                    <span className={`ncd-pill ${ev.isPublished ? "good" : ""}`}>{ev.isPublished ? "Live" : ev.status}</span>
                  </div>
                );
              })}
            </div>
          )}
        </article>

        {/* Pending bookings */}
        <article className="ncd-card">
          <div className="ncd-card-head">
            <div><h3>Needs Your Action</h3><p>Bookings waiting for confirmation</p></div>
            <span className="ncd-badge warn">{d.pending.length}</span>
          </div>
          {d.pending.length === 0 ? (
            <Empty text="Koi pending booking nahi hai." />
          ) : (
            <div className="ncd-rows">
              {d.pending.slice(0, 5).map((b) => (
                <div className="ncd-row" key={b._id}>
                  <div className="ncd-row-main">
                    <strong>{b.customerName || "Guest"}</strong>
                    <small>{b.eventId?.title || "Event"} · {b.ticketName} × {b.quantity}</small>
                  </div>
                  <div className="ncd-right">
                    <strong>{money(b.totalAmount)}</strong>
                    <span className={`ncd-pill ${b.paymentStatus === "paid" ? "good" : "warn"}`}>{b.paymentStatus}</span>
                  </div>
                </div>
              ))}
              <button className="ncd-btn" onClick={() => onNavigate("nightclub_bookings")}>Review all bookings</button>
            </div>
          )}
        </article>
      </section>

      <section className="ncd-quick">
        {[
          ["Add Event", "events_manage"],
          ["Event Tickets", "events_tickets"],
          ["Manage Tables", "nightclub_tables"],
          ["Table Pricing", "nightclub_table_pricing"],
          ["My Night Clubs", "nightclub_manage"],
        ].map(([label, go]) => (
          <button key={go} onClick={() => onNavigate(go)}>{label}<b>›</b></button>
        ))}
      </section>
    </div>
  );
}

function Empty({ text, action, onClick }) {
  return (
    <div className="ncd-empty">
      <p>{text}</p>
      {action && <button className="ncd-btn" onClick={onClick}>{action}</button>}
    </div>
  );
}

const CSS = `
.ncd{--line:#e4e1ee;--soft:#f5f4f9;--text:#1c1830;--muted:#6b657f;--accent:#5b3fd9;--accent-dark:#4a31b8;--accent-soft:#ece8fb;--good:#1a8a5a;--good-bg:#e5f6ee;--warn:#a76a00;--warn-bg:#fff3d9;--bad:#c42b46;--bad-bg:#fdeaee;color:var(--text);display:flex;flex-direction:column;gap:18px}
.ncd *{box-sizing:border-box}
.ncd h1,.ncd h2,.ncd h3,.ncd p{margin:0}
.ncd-head{display:flex;justify-content:space-between;align-items:flex-end;gap:12px;flex-wrap:wrap}
.ncd-head h1{font-size:28px}
.ncd-head p{color:var(--muted);margin-top:4px;font-size:14px}
.ncd-btn{background:#fff;color:var(--text);border:1px solid var(--line);border-radius:8px;padding:9px 16px;font:inherit;font-weight:600;cursor:pointer}
.ncd-btn:hover:not(:disabled){border-color:var(--muted)}
.ncd-btn:disabled{opacity:.5;cursor:not-allowed}
.ncd-btn.light{border-color:transparent}
.ncd-link{background:none;border:0;color:var(--accent);font:inherit;font-weight:600;cursor:pointer}
.ncd-alert{background:var(--bad-bg);color:var(--bad);border-radius:10px;padding:10px 14px;font-size:14px}

.ncd-banner{display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:wrap;background:linear-gradient(135deg,#2a1f6b,var(--accent));color:#fff;border-radius:14px;padding:20px 24px}
.ncd-banner h2{font-size:20px}
.ncd-banner p{margin-top:6px;font-size:14px;opacity:.9}

.ncd-metrics{display:grid;grid-template-columns:repeat(auto-fit,minmax(210px,1fr));gap:14px}
.ncd-metric{background:#fff;border:1px solid var(--line);border-radius:12px;padding:18px;display:flex;flex-direction:column;gap:6px}
.ncd-metric h3{font-size:14px;color:var(--muted);font-weight:600}
.ncd-value{font-size:32px;font-weight:700}
.ncd-metric p{font-size:11px;letter-spacing:.04em;color:var(--muted)}
.ncd-metric button{align-self:flex-start;margin-top:6px;background:none;border:0;padding:0;color:var(--accent);font:inherit;font-weight:600;cursor:pointer}

.ncd-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px}
@media(max-width:900px){.ncd-grid{grid-template-columns:1fr}}
.ncd-card{background:#fff;border:1px solid var(--line);border-radius:12px;padding:18px;display:flex;flex-direction:column;gap:14px}
.ncd-card-head{display:flex;justify-content:space-between;align-items:flex-start;gap:10px}
.ncd-card-head h3{font-size:17px}
.ncd-card-head p{font-size:13px;color:var(--muted);margin-top:3px}
.ncd-badge{background:var(--accent-soft);color:var(--accent-dark);font-size:12px;font-weight:700;padding:4px 10px;border-radius:99px}
.ncd-badge.warn{background:var(--warn-bg);color:var(--warn)}

.ncd-chart{display:flex;align-items:flex-end;gap:10px;height:190px;padding-top:6px}
.ncd-bar-col{flex:1;height:100%;display:flex;flex-direction:column;justify-content:flex-end;align-items:center;gap:4px;min-width:0}
.ncd-bar{width:100%;max-width:44px;background:var(--accent);border-radius:6px 6px 0 0;min-height:4px}
.ncd-bar-val{font-size:10px;color:var(--muted);white-space:nowrap;min-height:12px}
.ncd-bar-label{font-size:12px;color:var(--muted)}

.ncd-progress{height:8px;background:var(--soft);border-radius:99px;overflow:hidden}
.ncd-progress.sm{height:5px;margin:6px 0 3px}
.ncd-progress i{display:block;height:100%;background:var(--accent);border-radius:99px}
.ncd-list{display:flex;flex-direction:column;gap:6px}
.ncd-list button{display:flex;align-items:center;gap:10px;text-align:left;background:var(--soft);border:0;border-radius:8px;padding:10px 12px;font:inherit;cursor:pointer;color:var(--text)}
.ncd-list button span:nth-child(2){flex:1}
.ncd-list button.done{color:var(--muted)}
.ncd-list button.done span:nth-child(2){text-decoration:line-through}
.ncd-tick{width:20px;height:20px;border-radius:50%;border:1.5px solid var(--line);background:#fff;display:grid;place-items:center;font-size:12px;color:#fff}
.ncd-list .done .ncd-tick{background:var(--good);border-color:var(--good)}
.ncd-chips{display:flex;gap:6px;flex-wrap:wrap}
.ncd-chips span{font-size:12px;border:1px solid var(--line);border-radius:6px;padding:2px 8px;color:var(--muted)}
.ncd-chips .bad{color:var(--bad);border-color:var(--bad)}

.ncd-rows{display:flex;flex-direction:column;gap:12px}
.ncd-row{display:flex;align-items:center;gap:12px}
.ncd-row-main{flex:1;min-width:0}
.ncd-row-main strong{display:block;font-size:15px}
.ncd-row-main small{display:block;color:var(--muted);font-size:12px}
.ncd-date{background:var(--soft);border-radius:8px;padding:8px 10px;font-weight:700;font-size:13px;text-align:center;min-width:64px}
.ncd-right{text-align:right;display:flex;flex-direction:column;gap:4px;align-items:flex-end}
.ncd-pill{font-size:11px;font-weight:700;padding:3px 9px;border-radius:99px;background:var(--soft);color:var(--muted);text-transform:capitalize;white-space:nowrap}
.ncd-pill.good{background:var(--good-bg);color:var(--good)}
.ncd-pill.warn{background:var(--warn-bg);color:var(--warn)}
.ncd-empty{text-align:center;padding:24px 10px;color:var(--muted);display:flex;flex-direction:column;gap:10px;align-items:center}

.ncd-quick{display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:10px}
.ncd-quick button{display:flex;justify-content:space-between;align-items:center;background:#fff;border:1px solid var(--line);border-radius:10px;padding:14px 16px;font:inherit;font-weight:600;color:var(--text);cursor:pointer}
.ncd-quick button:hover{border-color:var(--accent);color:var(--accent)}
`;